export type Feature =
  boolean | { allowLocales: string[] } | { not: { allowLocales: string[] } };

export type FeaturesConfig<T extends string = string> = Partial<
  Record<T, Feature>
>;

/**
 * Resolves a feature config to a plain on/off map for one locale.
 *
 * An entry with no value is left out of the result rather than reported as off,
 * so a caller can tell "switched off here" from "not configured here".
 */
export function getFeatures<T extends string = string>(
  config: FeaturesConfig<T>,
  locale: string,
): Record<T, boolean> {
  return Object.entries(config).reduce<Record<string, boolean>>(
    (acc, [key, feature]) => {
      if (feature !== undefined && feature !== null) {
        acc[key] = evaluateFeature(feature as Feature, locale);
      }
      return acc;
    },
    {} as Record<T, boolean>,
  );
}

/**
 * Resolves a single feature entry, by asking each rule in turn until one
 * recognises the config.
 *
 * Funnel configs are plain JavaScript and are never checked against `Feature`,
 * so each rule confirms the shape it handles rather than trusting it. The `in`
 * operator throws on a primitive, which is what made a `feature: false` config
 * take the whole page down. An entry no rule recognises resolves to off, so a
 * malformed config hides its own feature instead of failing the render.
 */
function evaluateFeature(feature: Feature, locale: string): boolean {
  for (const rule of featureRules()) {
    const result = rule(feature, locale);

    if (result !== undefined) {
      return result;
    }
  }

  return false;
}

/**
 * One recognised way of writing a feature.
 *
 * A rule returns `undefined` for a config it does not recognise, which is how
 * it declines without claiming the config is off. Adding a shape means adding
 * a rule to the list below; no existing rule changes.
 */
type FeatureRule = (feature: unknown, locale: string) => boolean | undefined;

function featureRules(): FeatureRule[] {
  return [asWritten, whereLocaleIsAllowed, whereLocaleIsNotDenied];
}

function asWritten(feature: unknown): boolean | undefined {
  return typeof feature === 'boolean' ? feature : undefined;
}

function whereLocaleIsAllowed(
  feature: unknown,
  locale: string,
): boolean | undefined {
  return hasLocaleList(feature, 'allowLocales')
    ? feature.allowLocales.includes(locale)
    : undefined;
}

function whereLocaleIsNotDenied(
  feature: unknown,
  locale: string,
): boolean | undefined {
  return isObject(feature) && hasLocaleList(feature.not, 'allowLocales')
    ? !feature.not.allowLocales.includes(locale)
    : undefined;
}

/**
 * Both locale-based shapes carry their list under the same key, one directly
 * and one inside `not`, so both ask this rather than repeating the check.
 */
function hasLocaleList<K extends string>(
  value: unknown,
  key: K,
): value is Record<K, string[]> {
  return isObject(value) && Array.isArray(value[key]);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
